import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Chip,
  LinearProgress,
  Link,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RetrievedSource } from '../../services/api';
import styles from './SourcesList.module.css';

interface SourcesListProps {
  sources: RetrievedSource[];
  isLoading: boolean;
}

const SourcesList: React.FC<SourcesListProps> = ({ sources, isLoading }) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (isLoading) {
    return (
      <Paper className={styles.sourcesContainer} elevation={0}>
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            Retrieving sources...
          </Typography>
          <LinearProgress />
        </Box>
      </Paper>
    );
  }

  if (!sources || sources.length === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.sourcesContainer}>
        <Button 
          onClick={handleOpen}
          disableRipple
          className={styles.sourcesButton}
        >
          SOURCES ({sources.length})
        </Button>
      </div>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Sources ({sources.length})</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers className={styles.dialogContent}>
          {sources.map((source, index) => {
            let displayTitle;
            const meta = source.metadata || {};
            if (meta.url) {
              displayTitle = (
                <Link
                  href={meta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.sourceUrl}
                >
                  {meta.title || meta.url}
                </Link>
              );
            } else if (meta.filename) {
              displayTitle = meta.filename;
            } else {
              displayTitle = 'Unknown source';
            }

            return (
              <Box key={source.id || index} className={styles.sourceItem}>
                <Box className={styles.sourceHeader}>
                  <Typography variant="subtitle2" className={styles.sourceTitle}>
                    {displayTitle}
                  </Typography>
                  <Chip
                    label={`Score: ${(source.score * 100).toFixed(0)}%`}
                    size="small"
                    color={source.score > 0.8 ? "success" : source.score > 0.5 ? "primary" : "default"}
                    className={styles.scoreChip}
                  />
                </Box>
                <Typography variant="body2" className={styles.sourceFullContent}>
                  {source.content}
                </Typography>
                {index < sources.length - 1 && (
                  <Box className={styles.divider} />
                )}
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SourcesList;
